// reservebar-cart-modal.js
import {
	getLiquidAddressObject,
	getLiquidAddressString,
	getLiquidClient,
	getReservebarLabel,
	getDeliveryExpectationSubtitle,
	getReservebarUnavailableMessage,
	getRetailerPlacementRank,
	isRetailerPurchasable,
	normalizeCustomerPlacementForCart,
	normalizeLiquidProduct,
	syncLiquidSdkAddress
} from './reservebar-config';

import {
	generateAddressModal,
	reservebarLastProductMeta,
	setReservebarLastProductMeta
} from './reservebar-address-modal';

export function generateCartModal(liquidGroupingID, meta = {}) {
	if (document.querySelectorAll('.reservebar-cart').length > 0) {
		return false;
	}

	const productTitle = reservebarLastProductMeta.title || meta.title || '';
	const productAttr = reservebarLastProductMeta.attr || meta.attr || '';
	const productPoster = reservebarLastProductMeta.poster || meta.poster || '';

	const bodyTemplate = generateCartTemplateBody();
	const modal = document.createElement('div');
	modal.className = 'reservebar-cart';
	modal.innerHTML = `
    <div class="reservebar-cart__overlay"></div>
    <div class="reservebar-cart__content" liquid-id="${liquidGroupingID}">
      <div class="reservebar-cart__close"></div>
      <h4 class="reservebar-cart__title">${getReservebarLabel(
			'reservebar_cart_title',
			'Add to Cart'
		)}</h4>
      <div class="reservebar-cart__product">
        ${
			productPoster
				? `<div class="reservebar-cart__product-poster"><img src="${productPoster}" alt="${
						productTitle || ''
				  }"></div>`
				: ''
		}
        <div class="reservebar-cart__product-meta">
          <div class="reservebar-cart__product-name">${productTitle}</div>
          <div class="reservebar-cart__product-attr">${productAttr}</div>
        </div>
      </div>
      ${bodyTemplate}
    </div>
  `;

	document.body.appendChild(modal);
	document.body.classList.add('lock-position');

	generateCartSettings(liquidGroupingID, modal, meta);
	return true;
}

export function generateCartTemplateBody(options = {}) {
	const {includeShipping = true} = options;
	const shippingAddress = getLiquidAddressString();
	return `
      <div class="reservebar-cart__delivery">
        <span class="reservebar-cart__delivery-label">${getReservebarLabel(
			'reservebar_delivery_label',
			'Delivery Options'
		)}</span>
      </div>
      ${
			includeShipping
				? `<div class="reservebar-cart__shipping">
        <span class="reservebar-cart__shipping-label">${getReservebarLabel(
			'reservebar_shipping_label',
			'Shipping Address :'
		)}</span>
        <span class="reservebar-cart__shipping-value">${
			shippingAddress || ''
		}</span>
      </div>`
				: ''
		}
      <div class="reservebar-cart__options" data-reservebar-delivery-options></div>
      <div class="reservebar-cart__price" data-reservebar-price></div>
      <div class="rb-add-to-cart-ctas">
        <div class="reservebar-cart__qty">
          <button type="button" class="reservebar-cart__qty-btn reservebar-cart__qty-btn--minus" aria-label="${getReservebarLabel(
				'reservebar_qty_decrease_label',
				'Decrease quantity'
			)}">&minus;</button>
          <select class="reservebar-cart__qty-select">
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
          <button type="button" class="reservebar-cart__qty-btn reservebar-cart__qty-btn--plus" aria-label="${getReservebarLabel(
				'reservebar_qty_increase_label',
				'Increase quantity'
			)}">&plus;</button>
        </div>
        <div class="reservebar-cart__atc">
          <button type="button" disabled class="reservebar-cart__atc-btn">
            ${getReservebarLabel('reservebar_atc_label', 'Add to Cart')}
          </button>
        </div>
      </div>
      <div class="reservebar-cart__error" ></div>
  `;
}

export function generateCartSettings(liquidGroupingID, modal, meta = {}) {
	setReservebarLastProductMeta(meta);

	// Grab product title and attribute elements for later updating
	const productNameEl = modal.querySelector('.reservebar-cart__product-name');
	const productAttrEl = modal.querySelector('.reservebar-cart__product-attr');
	// Close handlers
	const closeModal = () => {
		if (modal.querySelector('.reservebar-cart__close')) {
			modal.remove();
			document.body.classList.remove('lock-position');
		}
	};
	if (modal.querySelector('.reservebar-cart__close')) {
		modal
			.querySelector('.reservebar-cart__close')
			.addEventListener('click', closeModal);
		modal
			.querySelector('.reservebar-cart__overlay')
			.addEventListener('click', closeModal);
	}

	// Make shipping address clickable → reopen Address modal
	const shippingValueEl = modal.querySelector(
		'.reservebar-cart__shipping-value'
	);
	if (shippingValueEl) {
		shippingValueEl.addEventListener('click', (e) => {
			e.preventDefault();
			const gid =
				modal
					.querySelector('.reservebar-cart__content')
					?.getAttribute('liquid-id') || liquidGroupingID;
			closeModal();
			if (gid) {
				generateAddressModal(gid, {
					...reservebarLastProductMeta,
					skipPrefillAvailabilityCheck: true
				});
			}
		});
	}

	// Delivery options + Add to Cart logic via Liquid APIs
	const optionsContainer = modal.querySelector(
		'[data-reservebar-delivery-options]'
	);
	const priceEl = modal.querySelector('[data-reservebar-price]');
	const qtySelect = modal.querySelector('.reservebar-cart__qty-select');
	const addToCartBtn = modal.querySelector('.reservebar-cart__atc-btn');
	const errorEls = modal.querySelectorAll('.reservebar-cart__error');
	const ctasEl = modal.querySelector('.rb-add-to-cart-ctas');

	let selectedVariant = null;
	let selectedRetailer = null;

	function setError(message) {
		if (!errorEls || !errorEls.length) return;
		// show the same message in all error containers
		errorEls.forEach((el) => {
			el.textContent = message || '';
		});
	}

	function formatPrice(price) {
		if (typeof price === 'number') {
			return `$${price.toFixed(2)}`;
		}
		const p = parseFloat(price);
		if (!isNaN(p)) {
			return `$${p.toFixed(2)}`;
		}
		return '';
	}

	function updatePriceDisplay() {
		if (!priceEl || !selectedRetailer) return;
		const qty = qtySelect ? parseInt(qtySelect.value, 10) || 1 : 1;
		const unitPrice = parseFloat(selectedRetailer.price || '0');
		if (isNaN(unitPrice)) {
			priceEl.textContent = '';
			return;
		}
		const total = unitPrice * qty;
		priceEl.textContent = formatPrice(total);
	}

	function selectOption(cardEl, variant, retailer) {
		selectedVariant = variant;
		selectedRetailer = retailer;

		// update qty options based on retailer stock
		if (
			selectedRetailer &&
			typeof selectedRetailer.inStock !== 'undefined'
		) {
			updateQtyOptions(selectedRetailer.inStock);
		}

		// visual active state
		if (optionsContainer) {
			optionsContainer
				.querySelectorAll('.reservebar-cart__option')
				.forEach((el) => {
					el.classList.remove('is-active');
				});
		}
		if (cardEl) {
			cardEl.classList.add('is-active');
		}

		if (addToCartBtn) {
			addToCartBtn.removeAttribute('disabled');
		}
		setError('');
		updatePriceDisplay();
	}

	function createOptionCard({
		label,
		subtitle,
		description,
		variant,
		retailer
	}) {
		if (!optionsContainer) return;

		const card = document.createElement('button');
		card.type = 'button';
		card.className = 'reservebar-cart__option';
		card.innerHTML = `
			<div class="reservebar-cart__option-header">${label}</div>
			<div class="reservebar-cart__option-subtitle">${subtitle}</div>
			<div class="reservebar-cart__option-desc">${description}</div>
		`;

		card.addEventListener('click', () => {
			selectOption(card, variant, retailer);
		});

		optionsContainer.appendChild(card);
	}

	async function loadProductAndOptions() {
		if (!optionsContainer || !addToCartBtn) return;

		optionsContainer.innerHTML = '';
		if (priceEl) priceEl.textContent = '';
		setError('');
		addToCartBtn.setAttribute('disabled', 'disabled');

		if (ctasEl) {
			ctasEl.style.display = 'none';
		}
		try {
			const liquid = await getLiquidClient();
			const params = getAddresParams(liquidGroupingID);
			const products = await liquid.product(params);
			const product = normalizeLiquidProduct(products, liquidGroupingID);
			if (!product || !Array.isArray(product.variants)) {
				if (ctasEl) {
					ctasEl.style.display = 'none';
				}
				setError(getReservebarUnavailableMessage(liquidGroupingID));
				return;
			}

			if (ctasEl) {
				ctasEl.style.display = '';
			}
			// Update product title and attributes from Liquid API
			if (productNameEl && product.name) {
				productNameEl.textContent = product.name;
				reservebarLastProductMeta.title = product.name;
			}

			if (productAttrEl) {
				const firstVariant = Array.isArray(product.variants)
					? product.variants[0]
					: null;

				const attrParts = [];
				// category, subType, type come from Liquid product response
				if (product.category) {
					attrParts.push(product.category);
				}
				if (product.subType) {
					attrParts.push(product.subType);
				}
				if (product.type) {
					attrParts.push(product.type);
				}
				// size is usually on the first variant (e.g. "750ml, bottle")
				if (firstVariant && firstVariant.size) {
					attrParts.push(firstVariant.size);
				}

				if (attrParts.length) {
					const attrString = attrParts.join(' · ');
					productAttrEl.textContent = attrString;
					reservebarLastProductMeta.attr = attrString;
				}
			}

			// Build fulfillment options from variants/retailers
			const options = [];

			product.variants.forEach((variant) => {
				if (!variant || !Array.isArray(variant.retailers)) return;

				const availabilities = Array.isArray(variant.availability)
					? variant.availability
					: [];

				variant.retailers.forEach((retailer) => {
					if (!isRetailerPurchasable(retailer, variant)) return;
					const type = (
						retailer.type ||
						availabilities[0] ||
						''
					).toLowerCase();

					const label =
						type === 'ondemand' || type === 'on_demand'
							? getReservebarLabel(
									'reservebar_cart_delivery_on_demand_label',
									'Delivery on demand'
							  )
							: getReservebarLabel(
									'reservebar_cart_delivery_standard_label',
									'Standard Shipping'
							  );

					options.push({
						key: type,
						label,
						subtitle: getDeliveryExpectationSubtitle(retailer, variant),
						description: 'Fulfilled by ' + retailer.retailer.name,
						variant,
						retailer
					});
				});
			});

			options.sort(
				(a, b) =>
					getRetailerPlacementRank(a.retailer, a.variant) -
					getRetailerPlacementRank(b.retailer, b.variant)
			);

			if (!options.length) {
				if (ctasEl) {
					ctasEl.style.display = 'none';
				}
				setError(getReservebarUnavailableMessage(liquidGroupingID));
				return;
			}
			if (ctasEl) {
				ctasEl.style.display = '';
			}
			options.forEach((opt) => {
				createOptionCard({
					label: opt.label,
					subtitle: opt.subtitle,
					description: opt.description,
					variant: opt.variant,
					retailer: opt.retailer
				});
			});

			// Auto-select first option
			const firstCard = optionsContainer.querySelector(
				'.reservebar-cart__option'
			);
			if (firstCard && options[0]) {
				selectOption(
					firstCard,
					options[0].variant,
					options[0].retailer
				);
			}
		} catch (err) {
			console.error('[ReserveBar] failed to load product options', err);
			setError(
				getReservebarLabel(
					'reservebar_cart_error_delivery',
					'Unable to load delivery options. Please try again.'
				)
			);
		}
	}
	function updateQtyOptions(maxStock) {
		if (!qtySelect) return;

		const max = Number(maxStock) > 0 ? Number(maxStock) : 1;
		const previousValue = parseInt(qtySelect.value, 10) || 1;

		qtySelect.innerHTML = '';

		for (let i = 1; i <= max; i++) {
			const opt = document.createElement('option');
			opt.value = String(i);
			opt.textContent = String(i);
			qtySelect.appendChild(opt);
		}

		const safeValue = previousValue <= max ? previousValue : max;
		qtySelect.value = String(safeValue);
	}

	async function addToCart() {
		if (!selectedRetailer) {
			setError(
				getReservebarLabel(
					'reservebar_cart_error_choose_option',
					'Please choose a delivery option.'
				)
			);
			return;
		}

		const qty = qtySelect ? parseInt(qtySelect.value, 10) || 1 : 1;
		const variantId = Number(selectedRetailer.variantId);

		if (!variantId) {
			console.error(
				'[ReserveBar] missing variantId on selectedRetailer',
				selectedRetailer
			);
			setError(
				getReservebarLabel(
					'reservebar_cart_error_add_reload',
					'Unable to add this product right now. Please reload the page and try again.'
				)
			);
			return;
		}

		// Optional: enforce stock one more time before adding
		if (typeof selectedRetailer.inStock !== 'undefined') {
			const max =
				Number(selectedRetailer.inStock) > 0
					? Number(selectedRetailer.inStock)
					: 1;
			if (qty > max) {
				setError(`Only ${max} in stock for this option.`);
				updateQtyOptions(max);
				return;
			}
		}

		try {
			addToCartBtn.disabled = true;
			addToCartBtn.textContent = getReservebarLabel(
				'reservebar_atc_adding_label',
				'Adding...'
			);
			setError('');

			const addrObj = getLiquidAddressObject();
			if (addrObj) {
				syncLiquidSdkAddress(addrObj, {resetCart: false});
			}

			const customerPlacement = normalizeCustomerPlacementForCart(
				selectedRetailer.customerPlacement,
				selectedVariant
			);

			// liquidSDK.cart.add defaults to customerPlacement: "standard" if omitted
			const result = liquidSDK.cart.add(variantId, qty, {
				customerPlacement
			});

			// If it returns a Promise, await it; otherwise just proceed
			if (result && typeof result.then === 'function') {
				await result;
			}

			if (typeof meta.onAddToCartSuccess === 'function') {
				meta.onAddToCartSuccess();
			}
			closeModal();
			if (
				meta.openLiquidCart &&
				typeof liquidSDK !== 'undefined' &&
				liquidSDK.cart &&
				typeof liquidSDK.cart.toggle === 'function'
			) {
				const cartEl = document.querySelector('div[liquid-cart]');
				if (cartEl && !cartEl.classList.contains('active')) {
					liquidSDK.cart.toggle();
				}
			}
		} catch (err) {
			console.error('[ReserveBar] failed to add to cart', err);
			setError(
				getReservebarLabel(
					'reservebar_cart_error_add_generic',
					'Unable to add to cart. Please try again.'
				)
			);
		} finally {
			addToCartBtn.textContent = getReservebarLabel(
				'reservebar_atc_label',
				'Add to Cart'
			);
			addToCartBtn.disabled = !selectedRetailer;
		}
	}
	if (qtySelect) {
		qtySelect.addEventListener('change', () => {
			if (
				selectedRetailer &&
				typeof selectedRetailer.inStock !== 'undefined'
			) {
				const max =
					Number(selectedRetailer.inStock) > 0
						? Number(selectedRetailer.inStock)
						: 1;
				let val = parseInt(qtySelect.value, 10) || 1;
				if (val > max) {
					val = max;
				}
				qtySelect.value = String(val);
			}
			updatePriceDisplay();
		});

		// The design calls for a +/- stepper rather than a native dropdown.
		// Keep the <select> as the source of truth (all stock-clamping and
		// price logic above already reacts to its "change" event) and just
		// drive it from two buttons instead of asking people to open a list.
		const qtyMinusBtn = modal.querySelector('.reservebar-cart__qty-btn--minus');
		const qtyPlusBtn = modal.querySelector('.reservebar-cart__qty-btn--plus');
		const stepQty = (direction) => {
			const nextIndex = qtySelect.selectedIndex + direction;
			if (nextIndex < 0 || nextIndex >= qtySelect.options.length) return;
			qtySelect.selectedIndex = nextIndex;
			qtySelect.dispatchEvent(new Event('change'));
		};
		if (qtyMinusBtn) {
			qtyMinusBtn.addEventListener('click', () => stepQty(-1));
		}
		if (qtyPlusBtn) {
			qtyPlusBtn.addEventListener('click', () => stepQty(1));
		}
	}
	if (addToCartBtn) {
		addToCartBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (typeof e.stopImmediatePropagation === 'function') {
				e.stopImmediatePropagation();
			}
			addToCart();
		});
	}

	// Load delivery options immediately
	loadProductAndOptions();

	return true;
}
export function getAddresParams(liquidGroupingID) {
	const addrObj = getLiquidAddressObject();
	let shipAddressParam = '';
	let shipStateParam = '';
	let latParam = null;
	let lngParam = null;

	if (addrObj) {
		const parts = [];
		if (addrObj.address1) parts.push(addrObj.address1);
		if (addrObj.address2) parts.push(addrObj.address2);

		const cityLine = [];
		if (addrObj.city) cityLine.push(addrObj.city);
		if (addrObj.state || addrObj.region)
			cityLine.push(addrObj.state || addrObj.region);
		if (addrObj.postalCode || addrObj.zip)
			cityLine.push(addrObj.postalCode || addrObj.zip);

		if (cityLine.length) parts.push(cityLine.join(', '));

		shipAddressParam = parts.join(', ');
		shipStateParam =
			addrObj.state || addrObj.region || addrObj.stateCode || '';
		latParam = addrObj.latitude || addrObj.lat || null;
		lngParam = addrObj.longitude || addrObj.lng || null;
	}
	const params = {};

	if (Array.isArray(liquidGroupingID)) {
		params.ids = liquidGroupingID;
	} else {
		params.ids = [String(liquidGroupingID)];
	}

	if (shipAddressParam) {
		params.shipAddress = shipAddressParam;
	} else if (shipStateParam) {
		params.shipState = shipStateParam;
	} else if (latParam !== null && lngParam !== null) {
		params.latitude = latParam;
		params.longitude = lngParam;
	}
	return params;
}
